import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '../config';
import { prepareHeaders } from './helpers';
import { ServiceTypes } from '../constants';
import { createBaseQueryWithReauth } from './baseQueryWithReauth';
import { AppDispatch } from '../store';
import { userRolesMap } from '@clatter/platform';
import { baseApi } from './baseApi';

const mapUsersByClatterId = (users: User[]) => {
  return users.reduce<UsersByClatterIdMap>((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});
};

const mapUsersByEmail = (users: User[]) => {
  return users.reduce<UsersByEmailMap>((acc, user) => {
    acc[user.email] = [
      {
        ...user,
        _id: user?.id,
      },
    ];
    return acc;
  }, {});
};

const usersApi = createApi({
  reducerPath: 'usersApi',
  keepUnusedDataFor: config.rtkCacheTimeout,
  baseQuery: createBaseQueryWithReauth(
    fetchBaseQuery({
      baseUrl: config.apiV2BaseUrl,
      prepareHeaders: prepareHeaders,
    }),
  ),
  tagTypes: [ServiceTypes.USERS],
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => result.data,
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST' }],
    }),
    getProvisionedUsers: builder.query<User[], void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) =>
        result.data.filter((user) =>
          user.roles?.some((r) =>
            [userRolesMap.ngAdmin, userRolesMap.ngUser, userRolesMap.ngContentManager].includes(r.role?.name),
          ),
        ),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST' }],
    }),
    getUsersByClatterId: builder.query<UsersByClatterIdMap, void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => mapUsersByClatterId(result.data),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST_BY_CLATTER_ID' }],
    }),
    getUsersByEmail: builder.query<UsersByEmailMap, void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => mapUsersByEmail(result.data),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST_BY_EMAIL' }],
    }),
    deleteUser: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST' }],
    }),
    getUserById: builder.query<User, { userId: number }>({
      query: ({ userId }) => `/user/${userId}`,
      transformResponse: (result: IApiV2Response<User>) => result.data as unknown as User,
      providesTags: (_result, _error, { userId }) => [{ type: ServiceTypes.USERS, id: userId }],
    }),
    sendVerificationEmail: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/user/${userId}/email-verify-requests`,
        method: 'POST',
      }),
    }),
  }),
});

// setEmailVerified, updateUser, and createUser hit the asset service, so they are injected into baseApi
const usersAssetEndpoints = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    setEmailVerified: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/v2/users/${userId}`,
        method: 'PATCH',
        body: { email_verified: true },
      }),
    }),
    updateUser: builder.mutation<void, { userId: number; data: Record<string, any> }>({
      query: ({ userId, data }) => ({
        url: `/v2/users/${userId}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    createUser: builder.mutation<void, { data: Record<string, any> }>({
      query: ({ data }) => ({
        url: `/v2/users`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const { useSetEmailVerifiedMutation, useUpdateUserMutation, useCreateUserMutation } = usersAssetEndpoints;

interface UsersApiCustomHelpers {
  invalidateAllTags: () => (dispatch: AppDispatch) => ReturnType<typeof usersApi.util.invalidateTags>;
}

type UsersApiWithCustom = typeof usersApi & {
  custom: UsersApiCustomHelpers;
};

const usersApiWithCustom: UsersApiWithCustom = Object.assign({}, usersApi, {
  custom: {
    invalidateAllTags: () => (dispatch: AppDispatch) => dispatch(usersApi.util.invalidateTags([ServiceTypes.USERS])),
  },
});

export { usersApiWithCustom as usersApi };

export const {
  useGetUsersQuery,
  useGetProvisionedUsersQuery,
  useGetUsersByEmailQuery,
  useGetUsersByClatterIdQuery,
  useGetUserByIdQuery,
  useDeleteUserMutation,
  useSendVerificationEmailMutation,
} = usersApi;
